import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Crosshair, Printer, ChevronDown } from 'lucide-react';
import { useResults } from '../lib/useResults';
import { SaveResultButton } from '../components/tools/SaveResultButton';
import { ToolSEOWrapper } from '../components/tools/ToolSEOWrapper';

const STATIONS = [
  { id: 'skierg', name: 'SkiErg', distance: '1000m', pct: 0.13, icon: '⛷' },
  { id: 'sled_push', name: 'Sled Push', distance: '50m', pct: 0.15, icon: '🏋' },
  { id: 'sled_pull', name: 'Sled Pull', distance: '50m', pct: 0.13, icon: '🪢' },
  { id: 'burpee_bj', name: 'Burpee Broad Jumps', distance: '80m', pct: 0.14, icon: '🦘' },
  { id: 'row', name: 'Rowing', distance: '1000m', pct: 0.13, icon: '🚣' },
  { id: 'farmers', name: 'Farmers Carry', distance: '200m', pct: 0.09, icon: '🧑‍🌾' },
  { id: 'lunges', name: 'Sandbag Lunges', distance: '100m', pct: 0.13, icon: '🏃' },
  { id: 'wall_balls', name: 'Wall Balls', distance: '75-100 reps', pct: 0.10, icon: '🎯' },
];

const DIVISIONS = [
  { value: 'open_m', label: 'Open Men' },
  { value: 'open_w', label: 'Open Women' },
  { value: 'pro_m', label: 'Pro Men' },
  { value: 'pro_w', label: 'Pro Women' },
  { value: 'doubles', label: 'Doubles' },
];

const PRESETS = [
  { label: '60 min (Elite)', totalSec: 3600 },
  { label: '1:15 (Competitive)', totalSec: 4500 },
  { label: '1:30 (Strong)', totalSec: 5400 },
  { label: '1:45 (Intermediate)', totalSec: 6300 },
  { label: '2:00 (Solid)', totalSec: 7200 },
  { label: '2:15 (Developing)', totalSec: 8100 },
  { label: '2:30 (Beginner)', totalSec: 9000 },
];

function parsePace(str) {
  const parts = str.split(':');
  if (parts.length === 2) return parseInt(parts[0]) * 60 + parseInt(parts[1]);
  return 0;
}

function fmtTime(sec) {
  if (!sec || sec <= 0) return '0:00';
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = Math.round(sec % 60);
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function fmtPace(secPerKm) {
  const m = Math.floor(secPerKm / 60);
  const s = Math.round(secPerKm % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

const Select = ({ label, value, options, onChange, testId }) => {
  const [open, setOpen] = useState(false);
  const selected = options.find(o => o.value === value);
  return (
    <div className="relative" data-testid={testId}>
      <label className="block text-zinc-400 text-xs uppercase tracking-wider mb-2 font-heading">{label}</label>
      <button type="button" onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 bg-zinc-800 border border-zinc-700 text-white text-sm hover:border-[#e8ff00]/50 transition-colors">
        <span>{selected?.label || 'Select...'}</span>
        <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-20 w-full mt-1 bg-zinc-800 border border-zinc-700 shadow-xl max-h-64 overflow-y-auto">
          {options.map(o => (
            <button key={o.value} type="button"
              onClick={() => { onChange(o.value); setOpen(false); }}
              className={`w-full text-left px-4 py-3 text-sm ${o.value === value ? 'bg-[#e8ff00]/20 text-white' : 'text-zinc-300 hover:bg-zinc-700'}`}>
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default function StationSplitCalculatorPage() {
  const [goalHours, setGoalHours] = useState('1');
  const [goalMinutes, setGoalMinutes] = useState('30');
  const [goalSeconds, setGoalSeconds] = useState('0');
  const [paceMin, setPaceMin] = useState('5');
  const [paceSec, setPaceSec] = useState('30');
  const [division, setDivision] = useState('open_m');
  const [transitionSec, setTransitionSec] = useState('15');
  const [result, setResult] = useState(null);
  const [saved, setSaved] = useState(false);
  const { saveResult } = useResults();
  const paceCardRef = useRef(null);

  const applyPreset = (totalSec) => {
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    setGoalHours(String(h));
    setGoalMinutes(String(m));
    setGoalSeconds(String(s));
  };

  const calculate = () => {
    const totalGoalSec = (parseInt(goalHours) || 0) * 3600 + (parseInt(goalMinutes) || 0) * 60 + (parseInt(goalSeconds) || 0);
    if (totalGoalSec <= 0) return;

    const pacePerKm = (parseInt(paceMin) || 0) * 60 + (parseInt(paceSec) || 0);
    if (pacePerKm <= 0) return;

    const totalRunSec = pacePerKm * 8;
    const totalTransitionSec = (parseInt(transitionSec) || 15) * 16;
    const totalStationSec = totalGoalSec - totalRunSec - totalTransitionSec;

    if (totalStationSec <= 0) {
      return;
    }

    const stationSplits = STATIONS.map(s => ({
      ...s,
      targetSec: Math.round(totalStationSec * s.pct),
    }));

    const runSplits = Array.from({ length: 8 }, (_, i) => ({
      id: `run_${i + 1}`,
      name: `Run ${i + 1}`,
      distance: '1km',
      targetSec: Math.round(pacePerKm),
    }));

    setResult({
      totalGoalSec,
      pacePerKm,
      totalRunSec,
      totalStationSec,
      totalTransitionSec,
      stationSplits,
      runSplits,
      runPct: ((totalRunSec / totalGoalSec) * 100).toFixed(0),
      stationPct: ((totalStationSec / totalGoalSec) * 100).toFixed(0),
    });
    setSaved(false);
  };

  const handlePrint = () => {
    if (!paceCardRef.current) return;
    const w = window.open('', '_blank');
    w.document.write(`<!DOCTYPE html><html><head><title>HYROX Pace Card</title>
      <style>body{font-family:sans-serif;padding:20px;max-width:700px;margin:0 auto}
      table{width:100%;border-collapse:collapse;margin:15px 0}th,td{padding:10px 12px;border:1px solid #333;text-align:left}
      th{background:#111;color:#fff}td{background:#1a1a1a;color:#e4e4e7}.accent{color:#e8ff00;font-weight:700}
      h1{color:#fff;font-size:24px}h2{color:#ccc;font-size:16px;margin-top:20px}
      .summary{display:flex;gap:20px;margin:15px 0}.summary div{padding:12px;background:#111;border:1px solid #333;text-align:center;flex:1}
      .summary .big{font-size:24px;font-weight:700;color:#e8ff00}
      .summary .lbl{font-size:10px;color:#999;text-transform:uppercase;letter-spacing:1px}
      body{background:#0a0a0a}@media print{body{background:#fff}th{background:#333}td{background:#f9f9f9;color:#111}.accent{color:#c00}.summary .big{color:#c00}}</style>
      </head><body>${paceCardRef.current.innerHTML}</body></html>`);
    w.document.close();
    w.print();
  };

  return (
    <ToolSEOWrapper toolKey="station-split">
    <main className="pt-20 min-h-screen" data-testid="station-split-page">
      <section className="container-ninja section-spacing">
        <Link to="/tools" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition-colors mb-8">
          <ArrowLeft className="w-4 h-4" /><span className="text-sm uppercase tracking-wider font-heading">Back to Tools</span>
        </Link>
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-block px-3 py-1 text-xs uppercase tracking-wider bg-[#e8ff00] text-black mb-4 font-heading">HYROX</span>
            <h1 className="text-4xl md:text-5xl text-white font-heading mb-3" data-testid="station-split-title">
              Station Split & Race Pace Planner
            </h1>
            <p className="text-zinc-400">Build your complete HYROX race plan with per-station targets and a printable pace card</p>
          </div>

          {/* Presets */}
          <div className="flex flex-wrap gap-2 justify-center mb-6">
            {PRESETS.map(p => (
              <button key={p.totalSec} onClick={() => applyPreset(p.totalSec)}
                className="px-3 py-1.5 text-xs border border-zinc-700 text-zinc-400 hover:border-[#e8ff00]/50 hover:text-[#e8ff00] transition-colors font-heading uppercase tracking-wider"
                data-testid={`preset-${p.totalSec}`}>
                {p.label}
              </button>
            ))}
          </div>

          <div className="bg-zinc-900/80 border border-zinc-800 p-6 md:p-8">
            {/* Goal Time */}
            <label className="block text-zinc-400 text-xs uppercase tracking-wider mb-2 font-heading">Goal Finish Time</label>
            <div className="grid grid-cols-3 gap-3 mb-4">
              {[
                { l: 'Hours', v: goalHours, s: setGoalHours, tid: 'goal-hours' },
                { l: 'Minutes', v: goalMinutes, s: setGoalMinutes, tid: 'goal-minutes' },
                { l: 'Seconds', v: goalSeconds, s: setGoalSeconds, tid: 'goal-seconds' },
              ].map(f => (
                <div key={f.l}>
                  <input type="number" value={f.v} onChange={e => f.s(e.target.value)} placeholder={f.l} min="0"
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 text-white text-sm text-center placeholder-zinc-500 focus:border-[#e8ff00] focus:outline-none transition-colors"
                    data-testid={f.tid} />
                  <p className="text-zinc-600 text-[10px] text-center mt-1 uppercase">{f.l}</p>
                </div>
              ))}
            </div>

            {/* Running Pace */}
            <label className="block text-zinc-400 text-xs uppercase tracking-wider mb-2 font-heading">Running Pace (per km)</label>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <input type="number" value={paceMin} onChange={e => setPaceMin(e.target.value)} placeholder="Min" min="0"
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 text-white text-sm text-center placeholder-zinc-500 focus:border-[#e8ff00] focus:outline-none transition-colors"
                  data-testid="pace-min" />
                <p className="text-zinc-600 text-[10px] text-center mt-1 uppercase">Minutes</p>
              </div>
              <div>
                <input type="number" value={paceSec} onChange={e => setPaceSec(e.target.value)} placeholder="Sec" min="0" max="59"
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 text-white text-sm text-center placeholder-zinc-500 focus:border-[#e8ff00] focus:outline-none transition-colors"
                  data-testid="pace-sec" />
                <p className="text-zinc-600 text-[10px] text-center mt-1 uppercase">Seconds</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <Select label="Division" value={division} options={DIVISIONS} onChange={setDivision} testId="division-select" />
              <div>
                <label className="block text-zinc-400 text-xs uppercase tracking-wider mb-2 font-heading">Transition Time (sec)</label>
                <input type="number" value={transitionSec} onChange={e => setTransitionSec(e.target.value)} min="5" max="60"
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 text-white text-sm text-center placeholder-zinc-500 focus:border-[#e8ff00] focus:outline-none transition-colors"
                  data-testid="transition-input" />
              </div>
            </div>

            <button onClick={calculate}
              className="w-full py-4 bg-gradient-to-r from-[#e8ff00] to-[#b8cc00] text-black font-heading text-lg uppercase tracking-wider transition-all hover:opacity-90"
              data-testid="calculate-splits-btn">
              Build My Race Plan
            </button>
          </div>

          {result && (
            <div className="mt-8 animate-entrance" data-testid="split-results">
              {/* Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                {[
                  { l: 'Goal Time', v: fmtTime(result.totalGoalSec), c: '#e8ff00' },
                  { l: 'Run Pace', v: `${fmtPace(result.pacePerKm)}/km`, c: '#00F0FF' },
                  { l: 'Total Running', v: `${fmtTime(result.totalRunSec)} (${result.runPct}%)`, c: '#00F0FF' },
                  { l: 'Total Stations', v: `${fmtTime(result.totalStationSec)} (${result.stationPct}%)`, c: '#E63946' },
                ].map(s => (
                  <div key={s.l} className="bg-zinc-900/80 border border-zinc-800 p-4 text-center">
                    <p className="text-zinc-500 text-[10px] uppercase tracking-wider mb-1">{s.l}</p>
                    <p className="text-lg font-heading" style={{ color: s.c }}>{s.v}</p>
                  </div>
                ))}
              </div>

              {/* Printable Pace Card */}
              <div ref={paceCardRef}>
                <div className="bg-zinc-900/80 border border-[#e8ff00]/30 p-6 md:p-8">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-white text-lg font-heading flex items-center gap-2">
                      <Crosshair className="w-5 h-5 text-[#e8ff00]" /> Race Pace Card
                    </h3>
                    <button onClick={handlePrint}
                      className="flex items-center gap-2 px-3 py-1.5 text-xs border border-zinc-700 text-zinc-400 hover:border-[#e8ff00]/50 hover:text-[#e8ff00] transition-colors"
                      data-testid="print-pace-card">
                      <Printer className="w-3 h-3" /> Print
                    </button>
                  </div>

                  {/* Race sequence table */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-zinc-700">
                          <th className="text-left py-3 px-3 text-zinc-400 text-xs uppercase tracking-wider font-heading">#</th>
                          <th className="text-left py-3 px-3 text-zinc-400 text-xs uppercase tracking-wider font-heading">Segment</th>
                          <th className="text-left py-3 px-3 text-zinc-400 text-xs uppercase tracking-wider font-heading">Distance</th>
                          <th className="text-right py-3 px-3 text-zinc-400 text-xs uppercase tracking-wider font-heading">Target</th>
                          <th className="text-right py-3 px-3 text-zinc-400 text-xs uppercase tracking-wider font-heading">Cumulative</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          let cumulative = 0;
                          const rows = [];
                          for (let i = 0; i < 8; i++) {
                            const run = result.runSplits[i];
                            const station = result.stationSplits[i];
                            cumulative += run.targetSec;
                            rows.push(
                              <tr key={`run_${i}`} className="border-b border-zinc-800/50">
                                <td className="py-2.5 px-3 text-zinc-600">{i * 2 + 1}</td>
                                <td className="py-2.5 px-3 text-[#00F0FF] font-heading">{run.name}</td>
                                <td className="py-2.5 px-3 text-zinc-500">{run.distance}</td>
                                <td className="py-2.5 px-3 text-right text-white font-mono">{fmtTime(run.targetSec)}</td>
                                <td className="py-2.5 px-3 text-right text-zinc-500 font-mono">{fmtTime(cumulative)}</td>
                              </tr>
                            );
                            cumulative += station.targetSec;
                            rows.push(
                              <tr key={`station_${i}`} className="border-b border-zinc-800/50 bg-zinc-800/20">
                                <td className="py-2.5 px-3 text-zinc-600">{i * 2 + 2}</td>
                                <td className="py-2.5 px-3 text-[#E63946] font-heading">{station.icon} {station.name}</td>
                                <td className="py-2.5 px-3 text-zinc-500">{station.distance}</td>
                                <td className="py-2.5 px-3 text-right text-white font-mono font-bold">{fmtTime(station.targetSec)}</td>
                                <td className="py-2.5 px-3 text-right text-zinc-500 font-mono">{fmtTime(cumulative)}</td>
                              </tr>
                            );
                          }
                          return rows;
                        })()}
                        <tr className="border-t-2 border-[#e8ff00]/30">
                          <td colSpan="3" className="py-3 px-3 text-[#e8ff00] font-heading uppercase tracking-wider">Finish</td>
                          <td className="py-3 px-3 text-right text-[#e8ff00] font-heading text-lg" data-testid="total-time">
                            {fmtTime(result.totalGoalSec)}
                          </td>
                          <td></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Station breakdown bars */}
              <div className="bg-zinc-900/80 border border-zinc-800 p-6 md:p-8 mt-4">
                <h3 className="text-white text-sm font-heading uppercase tracking-wider mb-4">Station Time Budget</h3>
                {result.stationSplits.map(s => {
                  const pct = ((s.targetSec / result.totalStationSec) * 100).toFixed(0);
                  return (
                    <div key={s.id} className="mb-3">
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-zinc-300 text-sm">{s.icon} {s.name}</span>
                        <span className="text-zinc-400 text-sm font-mono">{fmtTime(s.targetSec)}</span>
                      </div>
                      <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                        <div className="h-full rounded-full bg-gradient-to-r from-[#E63946] to-[#ff6b35] transition-all duration-700"
                          style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-6">
                <SaveResultButton
                  saved={saved}
                  onSave={() => {
                    saveResult('stationSplit', {
                      value: fmtTime(result.totalGoalSec),
                      label: `HYROX ${fmtTime(result.totalGoalSec)} | Pace: ${fmtPace(result.pacePerKm)}/km | Run: ${fmtTime(result.totalRunSec)} Stations: ${fmtTime(result.totalStationSec)}`,
                      goalTime: fmtTime(result.totalGoalSec),
                      pace: fmtPace(result.pacePerKm),
                      runTime: fmtTime(result.totalRunSec),
                      stationTime: fmtTime(result.totalStationSec),
                    });
                    setSaved(true);
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </section>
    </main>
    </ToolSEOWrapper>
  );
}
